export const TOKEN_INVALID_CODES = [1002, 1003, 1004, 1005] as const;

interface ResponseLikeError {
  response?: {
    status?: number;
    data?: { code?: unknown };
  };
}

export class OriginalAuthorizationRefreshError extends Error {
  constructor(cause: unknown) {
    super('Failed to refresh the original account authorization.', { cause });
    this.name = 'OriginalAuthorizationRefreshError';
  }
}

export class OriginalSessionTerminatedError extends Error {
  constructor(cause: unknown) {
    super('The original account session has ended.', { cause });
    this.name = 'OriginalSessionTerminatedError';
  }
}

export class OriginalAuthRefreshRaceError extends Error {
  constructor(cause: unknown) {
    super('The account switch session changed during original auth cleanup.', {
      cause,
    });
    this.name = 'OriginalAuthRefreshRaceError';
  }
}

const hasErrorName = (error: unknown, name: string): boolean =>
  !!error &&
  typeof error === 'object' &&
  (error as { name?: unknown }).name === name;

export const isOriginalAuthorizationRefreshError = (
  error: unknown,
): error is OriginalAuthorizationRefreshError =>
  hasErrorName(error, 'OriginalAuthorizationRefreshError');

export const isOriginalSessionTerminatedError = (
  error: unknown,
): error is OriginalSessionTerminatedError =>
  hasErrorName(error, 'OriginalSessionTerminatedError');

export const isOriginalSessionExpiredError = (error: unknown): boolean => {
  let currentError: unknown = error;
  for (let depth = 0; depth < 3; depth += 1) {
    if (!currentError || typeof currentError !== 'object') return false;
    if (hasErrorName(currentError, 'SessionExpiredError')) return true;
    currentError = (currentError as { cause?: unknown }).cause;
  }
  return false;
};

export const shouldAttemptTokenRefresh = (
  status: number | undefined,
  errorCode: unknown,
): boolean => {
  const normalizedErrorCode = Number(errorCode);
  return (
    status === 401 ||
    (Number.isFinite(normalizedErrorCode) &&
      TOKEN_INVALID_CODES.some((code) => code === normalizedErrorCode))
  );
};

/**
 * 원본 refresh 자체가 확정적으로 끝난 경우만 판별한다.
 * 일반 wrapper의 cause에 든 switch endpoint 401은 원본 세션 손실로 오인하지 않고,
 * 원본 refresh callback이 명시적으로 표시한 error만 unwrap한다.
 */
export const isDefinitiveOriginalRefreshFailure = (error: unknown): boolean => {
  if (isOriginalSessionTerminatedError(error)) return true;
  if (isOriginalSessionExpiredError(error)) return true;

  const candidate = isOriginalAuthorizationRefreshError(error)
    ? error.cause
    : error;
  if (!candidate || typeof candidate !== 'object') return false;
  if (isOriginalSessionExpiredError(candidate)) return true;

  const { response } = candidate as ResponseLikeError;
  const status = response?.status;
  const errorCode = Number(response?.data?.code);
  return (
    status === 401 ||
    status === 403 ||
    (Number.isFinite(errorCode) &&
      TOKEN_INVALID_CODES.some((code) => code === errorCode))
  );
};
